/**
 * utils/discoveryGrid.ts — FRONTEND MIRROR of supabase/functions/server/discoveryGridCore.ts.
 *
 * Generated from the server file: only this header and the artifact-cells, lifecycle
 * and scope imports differ. Do not edit by hand; edit the server core and re-run:
 *   scripts/sync-discovery-grid-mirror.sh
 * tests/server/discoveryGrid_test.ts asserts the two build identical grids.
 */


import { buildArtifactCells, isWorkTypeItem, type ArtifactCell, type ArtifactStatus, type GatingIssue, type WizardSessionRecord } from './artifactCells.ts';
import { isLocked, normalizeStatus } from './configItemLifecycle.ts';
import { effectiveScope, type DescopeMark, type ScopeMarks } from './discoveryScope.ts';

// ---------- Types ----------

export type CellLevel = 0 | 1 | 2 | 3 | 4 | 5;
export type CellKind = 'question' | 'artifact';
export type CellOwner = 'client' | 'consultant' | 'none';
export type ScopeSourceType = 'sow' | 'change_order' | 'carried_over';

export interface ScopeSource {
  type: ScopeSourceType;
  ref?: string;
  label?: string;
}

export interface CellOpen {
  /** `guided`: a category-level question answered in Guided Mode (ai_context:category) */
  type: 'question' | 'wizard' | 'route' | 'guided';
  configItemId?: string;
  questionId?: string;
  wizardId?: string;
  path?: string;
  category?: string;
}

export interface GridCell {
  id: string;
  kind: CellKind;
  questionId?: string;
  artifact?: ArtifactCell;
  text: string;
  level: CellLevel;
  levelLabel: string;
  blocked: boolean;
  gatingIssues: GatingIssue[];
  owner: CellOwner;
  recommended: boolean;
  changed: boolean;
  source: ScopeSource;
  open: CellOpen;
  answer?: unknown;
  answeredBy?: string;
  answeredAt?: string;
  hasGuidance: boolean;
  /** Set when the cell left this engagement (spec §4.10): drawn hollow, not counted. */
  descoped?: DescopeMark | null;
}

export interface GridBlock {
  /** Config item id, or `guided:<category>` for the category's guided-question block */
  configItemId: string;
  /** True for the category-level guided-question block (no config item behind it) */
  guided?: boolean;
  name: string;
  category: string;
  status?: string;
  spaceId?: string;
  isGlobal?: boolean;
  assignedTo?: string;
  assignedToName?: string;
  ballInCourt?: string;
  source: ScopeSource;
  cells: GridCell[];
  /** ceil(sqrt(cells)) — the near-square block shape of spec §4.5 */
  cols: number;
  /** in-scope cells (descoped cells are drawn but not counted) */
  total: number;
  locked: number;
  /** Whole block left this engagement */
  descoped?: DescopeMark | null;
}

export interface GridRegion {
  category: string;
  label: string;
  total: number;
  locked: number;
  blocked: number;
  blocks: GridBlock[];
}

export interface GridSummary {
  total: number;
  /** counts per level, index = level */
  byLevel: [number, number, number, number, number, number];
  blocked: number;
  descoped: number;
  percentLocked: number;
  court: { client: number; consultant: number; none: number };
}

export interface NotActionableItem {
  configItemId: string;
  name: string;
  category: string;
}

export interface DiscoveryGrid {
  regions: GridRegion[];
  summary: GridSummary;
  notActionable: NotActionableItem[];
}

export interface GridConfigItem {
  id: string;
  name: string;
  category?: string;
  status?: string;
  spaceId?: string;
  isGlobal?: boolean;
  assignedTo?: string;
  assignedToName?: string;
  ballInCourt?: string;
  source?: { type?: string; changeOrderId?: string; changeOrderRecordId?: string; fromEngagementId?: string; [key: string]: unknown } | null;
  customQuestions?: Array<{
    id: string;
    text?: string;
    questionText?: string;
    intent?: string;
    education?: unknown;
    helpText?: string;
    grouping?: { isPartOfWizard?: boolean; wizardId?: string; wizardName?: string; gridBehavior?: string };
  }>;
  discoveryData?: {
    answers?: Record<string, unknown>;
    answerSources?: Record<string, { source?: string; acceptedAs?: string; confirmedAt?: string; confirmedBy?: string } | undefined>;
    followUps?: Record<string, { open?: boolean } | undefined>;
    checked?: Record<string, { by?: string; at?: string } | undefined>;
    lastUpdatedBy?: string;
    lastUpdatedAt?: string;
  };
  /** Legacy shape written by the old progress-grid answer route. */
  questions?: Array<{ id?: string; text?: string; question?: string }>;
  answers?: Array<{ questionId: string; value?: unknown; answeredBy?: string; answeredAt?: string }>;
  /** Descope / carry marks (discoveryScopeCore) */
  scope?: ScopeMarks | null;
  [key: string]: unknown;
}

/** A category's guided questions (question bank + firm customisations) and the company's answers to them. */
export interface GuidedCategoryInput {
  category: string;
  questions: Array<{ id: string; questionText?: string; text?: string; helpText?: string; intent?: string }>;
  context?: {
    answers?: Record<string, { answer?: unknown; answeredAt?: string; answeredBy?: string; source?: string; acceptedAs?: string; confirmedAt?: string } | undefined>;
    followUps?: Record<string, { open?: boolean } | undefined>;
    checked?: Record<string, { by?: string; at?: string } | undefined>;
    scope?: ScopeMarks | null;
  } | null;
}

export interface BuildDiscoveryGridInput {
  items: GridConfigItem[];
  engagementConfig?: unknown | null;
  wizardSessions?: WizardSessionRecord[];
  /** Category-level guided questions; one block per category, drawn first in its region. */
  guided?: GuidedCategoryInput[];
  links: { companyId: string; sowId: string | null };
}

/** Cell id of a guided-question cell. */
export function guidedCellId(category: string, questionId: string): string {
  return `guided:${category}:${questionId}`;
}

/** Parse a cell id into its parts. Guided: `guided:<category>:<questionId>`; item: `<configItemId>:<questionId>`. */
export function parseCellId(cellId: string): { kind: 'guided'; category: string; questionId: string } | { kind: 'item'; configItemId: string; questionId: string } | { kind: 'artifact'; id: string } | null {
  if (!cellId) return null;
  if (cellId.startsWith('artifact:')) return { kind: 'artifact', id: cellId };
  if (cellId.startsWith('guided:')) {
    const rest = cellId.slice('guided:'.length);
    const i = rest.indexOf(':');
    if (i <= 0) return null;
    return { kind: 'guided', category: rest.slice(0, i), questionId: rest.slice(i + 1) };
  }
  const i = cellId.lastIndexOf(':');
  if (i <= 0) return null;
  return { kind: 'item', configItemId: cellId.slice(0, i), questionId: cellId.slice(i + 1) };
}

// ---------- Constants ----------

export const LEVEL_LABELS: Record<CellLevel, string> = {
  0: 'Not started',
  1: 'Suggested',
  2: 'Follow-up open',
  3: 'Answered',
  4: 'Checked',
  5: 'Locked',
};

/** Artifact cells skip levels 1 and 3 and name 2 and 4 differently (spec §4.3). */
export const ARTIFACT_LEVEL_LABELS: Record<CellLevel, string> = {
  0: 'Not started',
  1: 'Suggested',
  2: 'In progress',
  3: 'Answered',
  4: 'Complete',
  5: 'Locked',
};

export const OWNER_LABELS: Record<CellOwner, string> = {
  client: 'Client',
  consultant: 'Consultant',
  none: 'No action needed',
};

/** Canonical category order (spec §4.5); unknown categories sort after these, alphabetically. */
export const CATEGORY_ORDER: string[] = [
  'service_requests',
  'incidents',
  'changes',
  'problems',
  'workflows',
  'slas',
  'queues',
  'knowledge',
  'automation',
  'permissions',
  'integrations',
  'portal_config',
  'global_config',
  'assets',
  'custom_fields',
  'approvals',
  'other',
];

const CATEGORY_LABELS: Record<string, string> = {
  service_requests: 'Service Requests',
  incidents: 'Incidents',
  changes: 'Changes',
  problems: 'Problems',
  workflows: 'Workflows',
  slas: 'SLAs',
  queues: 'Queues',
  knowledge: 'Knowledge',
  automation: 'Automation',
  permissions: 'Permissions',
  integrations: 'Integrations',
  portal_config: 'Portal',
  global_config: 'Global Configuration',
  assets: 'Assets & CMDB',
  custom_fields: 'Custom Fields',
  approvals: 'Approvals',
  other: 'Other',
};

// ---------- Pure helpers ----------

export function categoryLabel(category: string): string {
  if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return String(category || 'other')
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function categoryRank(category: string): number {
  const i = CATEGORY_ORDER.indexOf(category);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

export function hasAnswer(answer: unknown): boolean {
  if (answer === undefined || answer === null) return false;
  if (typeof answer === 'string') return answer.trim() !== '';
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
}

/**
 * Level of one question cell (spec §5 derivation). `itemStatus` is the item's
 * lifecycle status (any spelling: `isLocked` folds it); a locked item is level 5
 * (docs/CONFIG_ITEM_LIFECYCLE.md). `followUp`, `checked` and
 * `answerSource.confirmedAt` are optional: until Phase C writes them, an
 * answered question is level 3 and an AI-inferred answer is level 1.
 */
export function questionLevel(
  itemStatus: string | undefined,
  answer: unknown,
  answerSource?: { source?: string; confirmedAt?: string } | null,
  followUp?: { open?: boolean } | null,
  checked?: { at?: string } | null,
): CellLevel {
  if (isLocked(itemStatus)) return 5;
  if (!hasAnswer(answer)) return 0;
  if (followUp?.open) return 2;
  if (answerSource?.source === 'ai_inferred' && !answerSource.confirmedAt) return 1;
  if (checked?.at) return 4;
  return 3;
}

export function artifactLevel(status: ArtifactStatus): CellLevel {
  switch (status) {
    case 'locked':
      return 5;
    case 'complete':
      return 4;
    case 'in_progress':
      return 2;
    case 'blocked':
      return 0;
    default:
      return 0;
  }
}

/** Ball-in-court from level (spec §4.4): client below Answered, consultant at Answered or blocked, none above. */
export function ownerForLevel(level: CellLevel, blocked: boolean): CellOwner {
  if (blocked) return 'consultant';
  if (level <= 2) return 'client';
  if (level === 3) return 'consultant';
  return 'none';
}

/** Near-square block: columns = ceil(sqrt(n)), minimum 1. */
export function blockColumns(n: number): number {
  return Math.max(1, Math.ceil(Math.sqrt(Math.max(0, n))));
}

/** Source after scope marks: a carry into this engagement overrides the item's own source. */
export function sourceWithScope(base: ScopeSource, scope: ScopeMarks | null | undefined, questionId: string | null, engagementId: string | null): { source: ScopeSource; descoped: DescopeMark | null } {
  const eff = effectiveScope(scope, questionId, engagementId);
  if (eff.carried) {
    const via = eff.carried.viaDocument === 'change_order' ? `Restored by ${eff.carried.ref ?? 'a change order'}` : 'Carried over from a previous engagement';
    return { source: { type: 'carried_over', ref: eff.carried.ref ?? eff.carried.fromEngagementId ?? undefined, label: eff.carried.label ?? via }, descoped: eff.descoped };
  }
  return { source: base, descoped: eff.descoped };
}

export function scopeSourceOf(item: GridConfigItem): ScopeSource {
  const t = item.source?.type;
  if (t === 'change_order') {
    const ref = item.source?.changeOrderId ?? item.source?.changeOrderRecordId;
    return { type: 'change_order', ref: ref ? String(ref) : undefined, label: ref ? `Added by ${ref}` : 'Added by a change order' };
  }
  if (t === 'carried_over') {
    return { type: 'carried_over', ref: item.source?.fromEngagementId ? String(item.source.fromEngagementId) : undefined, label: 'Carried over from a previous engagement' };
  }
  return { type: 'sow', label: 'Base SOW' };
}

// ---------- Build ----------

function questionCells(item: GridConfigItem, baseSource: ScopeSource, engagementId: string | null): GridCell[] {
  const cells: GridCell[] = [];
  const dd = item.discoveryData ?? {};
  const answers = dd.answers ?? {};
  const status = normalizeStatus(item);
  const blocked = status === 'blocked';
  const gating: GatingIssue[] = blocked ? [{ message: 'Config item is blocked' }] : [];
  const scoped = (qid: string) => sourceWithScope(baseSource, item.scope, qid, engagementId);

  const questions = item.customQuestions ?? [];
  if (questions.length > 0) {
    for (const q of questions) {
      if (!q || !q.id) continue;
      if (q.intent === 'exploratory') continue; // context-gathering, never a cell (spec §4.1)
      if (q.grouping?.gridBehavior === 'wizard_only') continue; // represented by the wizard artifact cell
      const answer = answers[q.id];
      const level = questionLevel(status, answer, dd.answerSources?.[q.id] ?? null, dd.followUps?.[q.id] ?? null, dd.checked?.[q.id] ?? null);
      const { source, descoped } = scoped(q.id);
      cells.push({
        id: `${item.id}:${q.id}`,
        kind: 'question',
        questionId: q.id,
        text: q.text || q.questionText || q.id,
        level,
        levelLabel: LEVEL_LABELS[level],
        blocked,
        gatingIssues: gating,
        owner: ownerForLevel(level, blocked),
        recommended: false,
        changed: false,
        source,
        open: { type: 'question', configItemId: item.id, questionId: q.id },
        answer: hasAnswer(answer) ? answer : undefined,
        answeredBy: hasAnswer(answer) ? dd.lastUpdatedBy : undefined,
        answeredAt: hasAnswer(answer) ? dd.lastUpdatedAt : undefined,
        hasGuidance: !!(q.education || q.helpText),
        descoped,
      });
    }
    return cells;
  }

  // Legacy progress-grid shape: `questions[]` with an `answers[]` array of { questionId, value }.
  if (Array.isArray(item.questions) && item.questions.length > 0) {
    const legacyAnswers = Array.isArray(item.answers) ? item.answers : [];
    item.questions.forEach((lq, index) => {
      const qid = lq?.id || `${item.id}_q_${index}`;
      const rec = legacyAnswers.find((a) => a && a.questionId === qid);
      const answer = rec?.value;
      const level = questionLevel(status, answer, null, dd.followUps?.[qid] ?? null, dd.checked?.[qid] ?? null);
      const { source, descoped } = scoped(qid);
      cells.push({
        id: `${item.id}:${qid}`,
        kind: 'question',
        questionId: qid,
        text: lq?.text || lq?.question || 'Untitled question',
        level,
        levelLabel: LEVEL_LABELS[level],
        blocked,
        gatingIssues: gating,
        owner: ownerForLevel(level, blocked),
        recommended: false,
        changed: false,
        source,
        open: { type: 'question', configItemId: item.id, questionId: qid },
        answer: hasAnswer(answer) ? answer : undefined,
        answeredBy: rec?.answeredBy,
        answeredAt: rec?.answeredAt,
        hasGuidance: false,
        descoped,
      });
    });
    return cells;
  }

  // Legacy items: answers without question definitions become one cell each.
  for (const [key, answer] of Object.entries(answers)) {
    if (!hasAnswer(answer)) continue;
    const level = questionLevel(status, answer, dd.answerSources?.[key] ?? null, dd.followUps?.[key] ?? null, dd.checked?.[key] ?? null);
    const { source, descoped } = scoped(key);
    cells.push({
      id: `${item.id}:${key}`,
      kind: 'question',
      questionId: key,
      text: categoryLabel(key),
      level,
      levelLabel: LEVEL_LABELS[level],
      blocked,
      gatingIssues: gating,
      owner: ownerForLevel(level, blocked),
      recommended: false,
      changed: false,
      source,
      open: { type: 'question', configItemId: item.id, questionId: key },
      answer,
      answeredBy: dd.lastUpdatedBy,
      answeredAt: dd.lastUpdatedAt,
      hasGuidance: false,
      descoped,
    });
  }
  return cells;
}

function artifactToCell(artifact: ArtifactCell, source: ScopeSource, descoped: DescopeMark | null = null): GridCell {
  const level = artifactLevel(artifact.status);
  const blocked = artifact.status === 'blocked' || artifact.gatingIssues.length > 0;
  const open: CellOpen =
    artifact.kind === 'wizard' && artifact.wizardId && artifact.configItemId
      ? { type: 'wizard', configItemId: artifact.configItemId, wizardId: artifact.wizardId }
      : { type: 'route', configItemId: artifact.configItemId, path: artifact.openPath };
  return {
    id: artifact.id,
    kind: 'artifact',
    artifact,
    text: artifact.label,
    level,
    levelLabel: ARTIFACT_LEVEL_LABELS[level],
    blocked,
    gatingIssues: artifact.gatingIssues,
    owner: ownerForLevel(level, blocked),
    recommended: false,
    changed: false,
    source,
    open,
    answer: level >= 4 ? 'Complete' : undefined,
    answeredAt: artifact.completedAt,
    hasGuidance: false,
    descoped,
  };
}

/** `ai_context:category` answers are a map keyed by question id; one legacy writer stored an array. */
function normalizeGuidedAnswers(answers: unknown): Record<string, any> {
  if (!answers) return {};
  if (Array.isArray(answers)) {
    const out: Record<string, any> = {};
    for (const a of answers) if (a && a.questionId) out[a.questionId] = a;
    return out;
  }
  return answers as Record<string, any>;
}

function guidedBlock(input: GuidedCategoryInput, engagementId: string | null): GridBlock | null {
  const questions = (input.questions ?? []).filter((q) => q && q.id && q.intent !== 'exploratory');
  if (questions.length === 0) return null;
  const ctx = { ...(input.context ?? {}), answers: normalizeGuidedAnswers(input.context?.answers) };
  const baseSource: ScopeSource = { type: 'sow', label: 'Base SOW' };
  const blockScope = sourceWithScope(baseSource, ctx.scope, null, engagementId);
  const cells: GridCell[] = questions.map((q) => {
    const rec = ctx.answers?.[q.id];
    const answer = rec?.answer;
    const level = questionLevel(undefined, answer, rec ? { source: rec.source, confirmedAt: rec.confirmedAt } : null, ctx.followUps?.[q.id] ?? null, ctx.checked?.[q.id] ?? null);
    const { source, descoped } = sourceWithScope(baseSource, ctx.scope, q.id, engagementId);
    return {
      id: guidedCellId(input.category, q.id),
      kind: 'question',
      questionId: q.id,
      text: q.questionText || q.text || q.id,
      level,
      levelLabel: LEVEL_LABELS[level],
      blocked: false,
      gatingIssues: [],
      owner: ownerForLevel(level, false),
      recommended: false,
      changed: false,
      source,
      open: { type: 'guided', category: input.category, questionId: q.id },
      answer: hasAnswer(answer) ? answer : undefined,
      answeredBy: hasAnswer(answer) ? rec?.answeredBy : undefined,
      answeredAt: hasAnswer(answer) ? rec?.answeredAt : undefined,
      hasGuidance: !!q.helpText,
      descoped,
    };
  });
  return finishBlock({
    configItemId: `guided:${input.category}`,
    guided: true,
    name: `${categoryLabel(input.category)} questions`,
    category: input.category,
    status: undefined,
    isGlobal: true,
    source: blockScope.source,
    cells,
    cols: 1,
    total: 0,
    locked: 0,
    descoped: blockScope.descoped,
  });
}

function finishBlock(block: GridBlock): GridBlock {
  block.cols = blockColumns(block.cells.length);
  const inScope = block.cells.filter((c) => !c.descoped);
  block.total = inScope.length;
  block.locked = inScope.filter((c) => c.level === 5).length;
  return block;
}

export function summarize(regions: GridRegion[]): GridSummary {
  const byLevel: GridSummary['byLevel'] = [0, 0, 0, 0, 0, 0];
  const court = { client: 0, consultant: 0, none: 0 };
  let total = 0;
  let blocked = 0;
  let descoped = 0;
  for (const r of regions) {
    for (const b of r.blocks) {
      for (const c of b.cells) {
        if (c.descoped) { descoped++; continue; }
        total++;
        byLevel[c.level]++;
        if (c.blocked) blocked++;
        court[c.owner]++;
      }
    }
  }
  return { total, byLevel, blocked, descoped, percentLocked: total ? Math.round((byLevel[5] / total) * 100) : 0, court };
}

export function buildDiscoveryGrid(input: BuildDiscoveryGridInput): DiscoveryGrid {
  const items = input.items ?? [];
  const artifacts = buildArtifactCells({
    items: items as any,
    engagementConfig: input.engagementConfig ?? null,
    wizardSessions: input.wizardSessions ?? [],
    links: input.links,
  });

  const blocksByItem = new Map<string, GridBlock>();
  const itemById = new Map(items.map((i) => [i.id, i]));
  const orderedItemIds: string[] = [];

  const sowId = input.links.sowId ?? null;
  const blockFor = (item: GridConfigItem): GridBlock => {
    let block = blocksByItem.get(item.id);
    if (!block) {
      const blockScope = sourceWithScope(scopeSourceOf(item), item.scope, null, sowId);
      block = {
        configItemId: item.id,
        name: item.name,
        category: item.category || 'other',
        status: normalizeStatus(item),
        spaceId: item.spaceId,
        isGlobal: item.isGlobal,
        assignedTo: item.assignedTo,
        assignedToName: item.assignedToName,
        ballInCourt: item.ballInCourt,
        source: blockScope.source,
        cells: [],
        cols: 1,
        total: 0,
        locked: 0,
        descoped: blockScope.descoped,
      };
      blocksByItem.set(item.id, block);
      orderedItemIds.push(item.id);
    }
    return block;
  };

  // Engagement-level artifacts (dashboards, space architecture) get a synthetic block each.
  const engagementBlocks: GridBlock[] = [];

  // Artifact cells first, so each block starts with its artifact (spec §4.5).
  for (const artifact of artifacts) {
    const item = artifact.configItemId ? itemById.get(artifact.configItemId) : undefined;
    if (item) {
      const block = blockFor(item);
      block.cells.push(artifactToCell(artifact, block.source, block.descoped ?? null));
    } else {
      engagementBlocks.push(
        finishBlock({
          configItemId: artifact.id,
          name: artifact.label,
          category: 'global_config',
          status: artifact.status,
          isGlobal: true,
          source: { type: 'sow', label: 'Base SOW' },
          cells: [artifactToCell(artifact, { type: 'sow', label: 'Base SOW' })],
          cols: 1,
          total: 1,
          locked: 0,
        }),
      );
    }
  }

  const notActionable: NotActionableItem[] = [];
  for (const item of items) {
    if (!item || !item.id) continue;
    if (isWorkTypeItem(item as any)) {
      // Represented by its work-type artifact cell (already added above).
      if (!blocksByItem.has(item.id)) notActionable.push({ configItemId: item.id, name: item.name, category: item.category || 'other' });
      continue;
    }
    const cells = questionCells(item, scopeSourceOf(item), sowId);
    if (cells.length === 0 && !blocksByItem.has(item.id)) {
      notActionable.push({ configItemId: item.id, name: item.name, category: item.category || 'other' });
      continue;
    }
    if (cells.length > 0) blockFor(item).cells.push(...cells);
  }

  // Guided (category-level) question blocks come first in their region.
  const guidedBlocks: GridBlock[] = [];
  for (const g of input.guided ?? []) {
    const b = guidedBlock(g, sowId);
    if (b) guidedBlocks.push(b);
  }

  const allBlocks = [...guidedBlocks, ...orderedItemIds.map((id) => finishBlock(blocksByItem.get(id)!)), ...engagementBlocks];

  const regionsByCategory = new Map<string, GridRegion>();
  for (const block of allBlocks) {
    if (block.cells.length === 0) continue;
    let region = regionsByCategory.get(block.category);
    if (!region) {
      region = { category: block.category, label: categoryLabel(block.category), total: 0, locked: 0, blocked: 0, blocks: [] };
      regionsByCategory.set(block.category, region);
    }
    region.blocks.push(block);
    region.total += block.total;
    region.locked += block.locked;
    region.blocked += block.cells.filter((c) => c.blocked && !c.descoped).length;
  }

  const regions = Array.from(regionsByCategory.values()).sort((a, b) => {
    const ra = categoryRank(a.category);
    const rb = categoryRank(b.category);
    if (ra !== rb) return ra - rb;
    return a.category.localeCompare(b.category);
  });

  return { regions, summary: summarize(regions), notActionable };
}

/** Flat list of every cell, in grid order. */
export function allCells(grid: DiscoveryGrid): GridCell[] {
  const out: GridCell[] = [];
  for (const r of grid.regions) for (const b of r.blocks) out.push(...b.cells);
  return out;
}

// ---------- Phase D: direction and growth (spec §4.6) ----------

/** What a viewer saw last time: cell id → level. Stored per user, company and engagement. */
export interface SeenSnapshot {
  at: string;
  levels: Record<string, CellLevel>;
}

export function snapshotOf(grid: DiscoveryGrid, at: string): SeenSnapshot {
  const levels: Record<string, CellLevel> = {};
  for (const c of allCells(grid)) levels[c.id] = c.level;
  return { at, levels };
}

/**
 * Mark cells that are new, or whose level moved, since the snapshot. With no
 * snapshot (first visit) nothing is marked: a first look should not be all dots.
 * Returns the number of changed cells.
 */
export function applySeenSnapshot(grid: DiscoveryGrid, snapshot: SeenSnapshot | null | undefined): number {
  let n = 0;
  for (const c of allCells(grid)) {
    const prev = snapshot ? snapshot.levels[c.id] : undefined;
    c.changed = !!snapshot && (prev === undefined || prev !== c.level);
    if (c.changed) n++;
  }
  return n;
}

export interface RecommendOptions {
  viewerRole: 'consultant' | 'client';
  viewerId?: string | null;
  limit?: number;
}

/**
 * Up to `limit` recommended-next cells (spec §4.6): unblocked question cells the
 * viewer can act on, preferring blocks assigned to the viewer, lowest level
 * first, in grid order. Consultants are pointed at answers to check (level 3)
 * then suggestions to confirm (level 1); clients at their open questions
 * (levels 0, 2, 1 in that order). Sets `cell.recommended` and returns the ids.
 */
export function recommendNext(grid: DiscoveryGrid, opts: RecommendOptions): string[] {
  const limit = Math.max(0, opts.limit ?? 5);
  const order: Record<string, number[]> = { consultant: [3, 1], client: [0, 2, 1] };
  const wanted = order[opts.viewerRole] ?? order.client;
  const candidates: Array<{ cell: GridCell; rank: number; index: number }> = [];
  let index = 0;
  for (const r of grid.regions) {
    for (const b of r.blocks) {
      const mine = !!opts.viewerId && b.assignedTo === opts.viewerId;
      for (const c of b.cells) {
        c.recommended = false;
        index++;
        if (c.kind !== 'question' || c.blocked || c.descoped) continue;
        const pos = wanted.indexOf(c.level);
        if (pos === -1) continue;
        candidates.push({ cell: c, rank: (mine ? 0 : 100) + pos, index });
      }
    }
  }
  candidates.sort((a, b) => a.rank - b.rank || a.index - b.index);
  const picked = candidates.slice(0, limit).map((x) => x.cell);
  for (const c of picked) c.recommended = true;
  return picked.map((c) => c.id);
}
